import type { Hand, RhythmDensity, ScaleCell } from 'Types/ExerciseContentTypes';
import type { ExerciseProgressPoint, ScaleProgressCell } from 'Types/ExerciseProgressTypes';
import { cellKey } from 'Reducers/exerciseProgressReducer';
import type {
  ExercisePrePracticeReadModel,
  PrePracticeTier,
  TierProgressMap,
  TierState,
} from './prePracticeReadModel';
import { tierProgressKey } from './prePracticeReadModel';

/**
 * MUS-480: derive the pre-practice screen's per-tier grip (`TierProgressMap`) from
 * the live hydrated per-cell frontier (the MUS-318 slice), replacing the static
 * fixture as the `progress` source. This is the honest realization of Decision
 * #117.5 - "a cell's tier is the highest waypoint its frontier dominates" - not a
 * flat any-earned-point read: each B/I/A tier is a full-config WAYPOINT, and a tier
 * lights only when a real frontier point DOMINATES that waypoint on every dimension.
 *
 * Server-authoritative: this reads only what the backend returned (the hydrated
 * cells). It never infers grip from a local event - the frontier is the single
 * source of truth (#124).
 *
 * Deliberate honesty boundaries (do NOT fabricate past them):
 *   - 'attempted' is never produced. The frontier stores EARNED (cleared/mastered)
 *     points only, so a played-but-not-cleared waypoint reads 'untouched'. There is
 *     nothing hydrated from which to derive 'attempted' today; collapsing it to
 *     'untouched' is correct, not a gap being papered over. (The 'attempted' state
 *     stays in the type for when an attempt signal is hydratable - MUS-459+.)
 *   - Review levels are left entirely untouched. A motor review level is a
 *     continuity run - "play N cleared cells unbroken" (#117.7) - not per-cell
 *     waypoint domination, and no continuity-run record is hydrated. Deriving its
 *     tiers from content-cell domination would fabricate a milestone the user has
 *     not earned, so a review level renders untouched until that record exists.
 */

/**
 * Aggregation policy across the several cells a level scopes (Level 1 = C, G, D).
 * A tier is a per-cell concept (#117.5), but the overview screen shows one glyph
 * per (level, tier), so the per-cell tiers must fold into one level-tier state.
 *
 * ALL-cell: a level's tier lights only when EVERY scoped cell has reached it - "you
 * have this waypoint on every key in the level". Chosen (Steven, 2026-09-19) so the
 * overview never over-reads: a mastered star on a level means the level is mastered,
 * not that one key of three is. The looser ANY-cell reading ("reached the waypoint
 * somewhere in this level") is the alternative; `foldCellStates` switches on the
 * `AGGREGATION` constant below. An untouched key therefore holds the whole level
 * untouched, so partial progress is deliberately not surfaced on this calm overview
 * by design - the per-key breakdown is the deeper Phase-3 level surface. This
 * is the single product-judgment knob in this module. NOTE: all-cell also sharpens
 * the case for one `scale x tonic` cell per level - captured as a design
 * consideration, not resolved here.
 */
const AGGREGATION: 'any-cell' | 'all-cell' = 'all-cell';

const HAND_IS_HT = (hand: Hand): boolean => hand === 'HT';
const RHYTHM_RANK: Record<RhythmDensity, number> = { quarter: 0, eighth: 1, sixteenth: 2 };

/**
 * Does frontier point `p` dominate tier waypoint `w`? True only when `p` is at
 * least as demanding as `w` on EVERY frontier dimension, per each dimension's own
 * order (#117.1):
 *   - hand: HT dominates RH and LH (playing hands together is playing each). RH and
 *     LH are incomparable, so a point matches a same-hand waypoint or an HT point
 *     dominates any hand waypoint.
 *   - octave span: a chain - more octaves dominate fewer.
 *   - direction: 'both' dominates 'asc' and 'desc'; 'asc'/'desc' match only themselves.
 *   - rhythm density: a chain quarter -> eighth -> sixteenth - denser dominates.
 *   - tempo: a scalar, meets-or-beats (>=), never exact-match (#117.9).
 */
export function pointDominatesWaypoint(p: ExerciseProgressPoint, w: PrePracticeTier): boolean {
  const handDominates = HAND_IS_HT(p.hand) || p.hand === w.hand;
  const octaveDominates = p.range_octaves >= w.rangeOctaves;
  const directionDominates = p.direction === 'both' || p.direction === w.direction;
  const rhythmDominates = RHYTHM_RANK[p.rhythm_density] >= RHYTHM_RANK[w.rhythmDensity];
  const tempoDominates = p.tempo >= w.tempo;
  return (
    handDominates && octaveDominates && directionDominates && rhythmDominates && tempoDominates
  );
}

/**
 * The tier state a single cell's frontier confers on one waypoint:
 *   - 'mastered' if a DOMINATING point is itself mastered (a flawless take at least
 *     as hard as the waypoint - #117.5 mastery survives as the accuracy gate on the
 *     dominating point);
 *   - 'cleared' if any point dominates but none of the dominating ones is mastered;
 *   - 'untouched' if no point dominates.
 * Mastery is read from the dominating point, never from some other easier point in
 * the cell - flawlessness at an easier config does not imply the waypoint is mastered.
 */
function cellTierState(cell: ScaleProgressCell | undefined, waypoint: PrePracticeTier): TierState {
  const dominating = (cell?.frontier ?? []).filter((p) => pointDominatesWaypoint(p, waypoint));
  if (dominating.length === 0) return 'untouched';
  return dominating.some((p) => p.mastered) ? 'mastered' : 'cleared';
}

const STATE_RANK: Record<TierState, number> = {
  untouched: 0,
  attempted: 1,
  cleared: 2,
  mastered: 3,
};

/** Fold the per-cell states of one level into a single level-tier state per the
 *  aggregation policy. ANY-cell takes the strongest; ALL-cell takes the weakest
 *  (and an empty level - no scoped cells - is 'untouched' either way). */
function foldCellStates(states: TierState[]): TierState {
  if (states.length === 0) return 'untouched';
  const pick = AGGREGATION === 'any-cell' ? Math.max : Math.min;
  const rank = pick(...states.map((s) => STATE_RANK[s]));
  return (
    (Object.keys(STATE_RANK) as TierState[]).find((s) => STATE_RANK[s] === rank) ?? 'untouched'
  );
}

/**
 * Build the `TierProgressMap` (keyed by `tierProgressKey(levelNumber, tierNumber)`)
 * from the read model's structure and this exercise's hydrated cells. Only non-
 * 'untouched' entries are emitted - a missing key reads as 'untouched' by contract,
 * so the map stays the grip (the cleared region), not a dense grid. An empty/absent
 * slice therefore yields `{}`, and the screen renders every tier untouched.
 *
 * `cellsByKey` is the slice's `byExercise[exerciseId]` map (cellKey -> cell); pass
 * `{}` when the exercise has no hydrated cells.
 */
export function deriveTierProgress(
  readModel: ExercisePrePracticeReadModel,
  cellsByKey: Record<string, ScaleProgressCell>,
): TierProgressMap {
  const progress: TierProgressMap = {};

  for (const level of readModel.levels) {
    // A review level's grip is a continuity run, not waypoint domination (#117.7),
    // and that record is not hydrated - leave every tier untouched (omitted).
    if (level.isReview) continue;

    const scopedCells: (ScaleProgressCell | undefined)[] = level.cells.map(
      (cell: ScaleCell) => cellsByKey[cellKey(cell)],
    );

    for (const tier of level.tiers) {
      const folded = foldCellStates(scopedCells.map((cell) => cellTierState(cell, tier)));
      if (folded !== 'untouched') {
        progress[tierProgressKey(level.levelNumber, tier.tierNumber)] = folded;
      }
    }
  }

  return progress;
}
